// ─────────────────────────────────────────────────────────────────────────────
// NotificationsBell – Glocken-Icon in der Topbar (FEAT-011)
//
// Zeigt:
//   - Counter (Badge) mit Anzahl ungelesener Notifications
//   - Dropdown-Liste der neuesten 50 Notifications beim Klick
//   - Klick auf Eintrag: markiert als gelesen + navigiert zum Link
//   - "Alle als gelesen" Aktion
//
// Polling alle 60 Sekunden – kein WebSocket nötig, reicht für niedrige Frequenz.
// ─────────────────────────────────────────────────────────────────────────────
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Badge, IconButton, Menu, MenuItem, Typography, Box, Divider, Button,
  Tooltip, alpha,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Notifications as NotificationsIcon,
  NotificationsNone,
  DoneAll,
  Assignment,
} from '@mui/icons-material';
import api from '../services/api';
import { updateBadge } from '../utils/badge';

function getRelativeTime(dateStr: string) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'gerade eben';
  if (mins < 60) return `vor ${mins} Min.`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `vor ${hours} Std.`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `vor ${days} Tag${days > 1 ? 'en' : ''}`;
  return new Date(dateStr).toLocaleDateString('de-DE');
}

export default function NotificationsBell() {
  const navigate = useNavigate();
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [count, setCount] = useState(0);
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  // Counter regelmäßig pollen (alle 60s + bei Tab-Fokus)
  const fetchCount = useCallback(() => {
    api.get('/notifications/unread-count')
      .then(res => {
        const c = res.data.count || 0;
        setCount(c);
        // Tab-Title + App-Badge + Favicon-Overlay synchron halten (FEAT-022)
        updateBadge(c);
      })
      .catch(() => {/* still – evtl. nicht eingeloggt o.ä. */});
  }, []);

  useEffect(() => {
    fetchCount();
    const interval = setInterval(fetchCount, 60000);
    const onFocus = () => fetchCount();
    window.addEventListener('focus', onFocus);
    return () => { clearInterval(interval); window.removeEventListener('focus', onFocus); };
  }, [fetchCount]);

  // Beim Öffnen die Liste laden
  const handleOpen = (e: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget);
    setLoading(true);
    api.get('/notifications')
      .then(res => setItems(res.data || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  };

  const handleClose = () => setAnchorEl(null);

  // Eintrag klicken: markiere als gelesen, navigiere zum Link
  const handleItemClick = async (n: any) => {
    if (!n.read_at) {
      try {
        await api.patch(`/notifications/${n.id}/read`);
        setCount(c => {
          const newCount = Math.max(0, c - 1);
          updateBadge(newCount);
          return newCount;
        });
        setItems(prev => prev.map((x: any) => x.id === n.id ? { ...x, read_at: new Date().toISOString() } : x));
      } catch {/* ignore */}
    }
    handleClose();
    if (n.link) navigate(n.link);
  };

  const handleMarkAllRead = async () => {
    try {
      await api.patch('/notifications/read-all');
      setCount(0);
      updateBadge(0);
      setItems(prev => prev.map((x: any) => x.read_at ? x : { ...x, read_at: new Date().toISOString() }));
    } catch {/* ignore */}
  };

  return (
    <>
      <Tooltip title="Benachrichtigungen">
        <IconButton onClick={handleOpen} size="large">
          <Badge badgeContent={count} color="error" max={99}>
            {count > 0 ? <NotificationsIcon /> : <NotificationsNone />}
          </Badge>
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={handleClose}
        PaperProps={{ sx: { width: 380, maxHeight: 500 } }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="subtitle1" fontWeight={700}>Benachrichtigungen</Typography>
          {count > 0 && (
            <Button size="small" startIcon={<DoneAll sx={{ fontSize: 16 }} />} onClick={handleMarkAllRead} sx={{ fontSize: '0.75rem' }}>
              Alle gelesen
            </Button>
          )}
        </Box>
        <Divider />
        {loading ? (
          <Box sx={{ p: 3, textAlign: 'center' }}><Typography variant="caption" color="text.secondary">Wird geladen...</Typography></Box>
        ) : items.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <NotificationsNone sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
            <Typography variant="body2" color="text.secondary">Keine Benachrichtigungen</Typography>
          </Box>
        ) : (
          items.map((n: any) => (
            <MenuItem
              key={n.id}
              onClick={() => handleItemClick(n)}
              sx={{
                py: 1.2, px: 2, whiteSpace: 'normal', alignItems: 'flex-start', gap: 1.2,
                bgcolor: n.read_at ? 'transparent' : alpha(theme.palette.primary.main, 0.06),
                '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.10) },
                borderLeft: n.read_at ? '3px solid transparent' : `3px solid ${theme.palette.primary.main}`,
              }}
            >
              <Box sx={{
                width: 32, height: 32, borderRadius: '50%', flexShrink: 0, mt: 0.3,
                bgcolor: alpha(theme.palette.info.main, 0.15), color: 'info.main',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Assignment sx={{ fontSize: 18 }} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" fontWeight={n.read_at ? 500 : 700} sx={{ fontSize: '0.85rem' }}>
                  {n.title}
                </Typography>
                {n.body && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.75rem', whiteSpace: 'normal' }}>
                    {n.body}
                  </Typography>
                )}
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                  {getRelativeTime(n.created_at)}
                </Typography>
              </Box>
            </MenuItem>
          ))
        )}
      </Menu>
    </>
  );
}
